import axios, { AxiosHeaders, type AxiosError, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { keepaliveAllowsPreflight } from '@/api/keepaliveSupport';
import { z } from 'zod';
import { convertKeysToCamel, convertKeysToSnake } from '@/lib/dataTransform';
import { useStaffStore } from '@/store';
import { useMergeSuggestionStore } from '@/store/useMergeSuggestionStore';
import { parseDenialEnvelope } from './mergeDenial';
import { getToken, clearToken } from '@/auth/session';
import { getCurrentUser, clearCurrentUser } from '@/auth/authData';
import { addBreadcrumb } from '@/lib/breadcrumbs';
import { stripLinkSegment } from '@/lib/urlExposure';
import { registerLocalEcho } from '@/features/files/localEcho';
import { fileExtension } from '@/features/files/fileExtension';
import { reportClientError } from '@/api/clientErrors';
import { traceIdFromError } from '@/api/errorTrace';
import { newTraceId, newSpanId, buildTraceparent, buildBaggage, requestTraceId } from '@/lib/traceContext';
import {
  READ_YOUR_WRITES_HEADER,
  isMutatingMethod,
  noteWrite,
  shouldDeclareReadYourWrites,
} from '@/api/readYourWrites';
import { authFailureError, classifyBounceReason, readDetail, readDetailError } from '@/api/authDetail';
// Plain data module (route strings); no runtime import cycle back into api/.
import { paths } from '@/constants';
import { authRouteHref, viewReturnAddress } from '@/features/accounts/lib/authRouteHref';
import { classifyConsumeError, consumeErrorCode, isExpectedConsumeRefusal, isTransportFailure, transportReportDims } from '@/api/consumeErrorPolicy';
import { BrowserAbortError, classifyFailure, isAccountMergedRetry, isTransportTimeout } from '@/api/failureClass';
import { chargeKeepaliveBytes, keepaliveBody, utf8Bytes } from '@/api/keepaliveBudget';
import { isConsumeStatePut, isSlowConsumeFailure, type AttemptStamped } from '@/api/consumeSlowFailure';
import { FetchTimeoutError, fetchWithTimeout } from '@/api/fetchWithTimeout';
import {
  ACCOUNT_MERGED_RETRY,
  idempotentWriteRoute,
  isReadMethod,
  parseRetryBody,
  SERVER_RETRY_CODES,
  serverRetryRoute,
} from '@/api/retryPolicy';
import {
  recordEndpointSuccess,
  recordEndpointFailure,
  admitRequest,
  releaseProbe,
  isEndpointCircuitOpen,
  anyCircuitOpen,
  configureCircuitRegistry,
  resetCircuitRegistry,
} from './circuitRegistry';
import {
  noteApiResult,
  configureConnectivity,
  resetConnectivity,
} from '@/connectivity/connectivityController';

// Backend: vingapi (FastAPI) - default port 8001 for local development
const BASE_URL = import.meta.env.VITE_API_URL || '';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Field names whose subtree must NOT be case-converted for THIS request
     * only — e.g. raw third-party `workers` JSON from the ADP sample endpoint.
     * Added on top of the global OPAQUE_KEYS so a name can stay structural
     * (camelCased) on every other endpoint. See lib/dataTransform.
     */
    opaqueKeys?: string[];
    /**
     * Opt THIS request out of the global 401 → `/login/signIn` bounce: the
     * caller owns UX + reporting for a 401 on this request. Only the explicit
     * token-exchange calls set it (`exchangeToken` in api/auth.ts, used by the
     * `/sso` page and by SignIn after a password sign-in). A flagged 401 rejects with
     * no side effects at all — no hook, no token clear, no report, no redirect
     * — so the caller's inline error can render instead of the page unloading.
     * Every other caller, including plain `getMe(token)`, keeps the bounce (W6).
     */
    skipAuthRedirect?: boolean;
  }
}

// =============================================================================
// GLOBAL RETRY + CIRCUIT BREAKER
// =============================================================================
// Prevents request storms: retries with backoff, then stops after too many failures

let RETRY_CONFIG = {
  maxRetries: 3,              // Max retries per request
  baseDelayMs: 1000,          // Initial backoff: 1 second
  maxDelayMs: 10000,          // Max backoff: 10 seconds
  retryableStatuses: [408, 429, 500, 502, 503, 504], // Transient failures worth retrying
};

/** Override retry config (e.g., disable retries in tests). */
export function configureRetries(overrides: Partial<typeof RETRY_CONFIG>) {
  RETRY_CONFIG = { ...RETRY_CONFIG, ...overrides };
}

/**
 * The status-based retry budget client.ts gives a retryable 5xx: a request is
 * made 1 + this many times. Read live (tests and ops override it through
 * `configureRetries`), so a caller that must match the budget never copies it.
 */
export function getMaxRetries(): number {
  return RETRY_CONFIG.maxRetries;
}

// =============================================================================
// C5: per-endpoint circuit registry + app-wide connectivity controller
// =============================================================================
// The old single module-global `circuitOpen` froze the WHOLE app the moment any
// ONE endpoint failed 5× — it conflated "this request always errors" with "the
// connection is down". It is replaced by two scoped mechanisms (see the C5
// design doc):
//   - circuitRegistry (Tier 1): a per-`${method} ${normalizeRoute(url)}`
//     circuit. A RESPONSE — even 500/503/409 — proves the server is reachable,
//     so response-bearing 5xx are scoped to their own key; ANY success on the
//     key resets it; recovery is a half-open single probe with growing backoff.
//     4xx gate nothing (no circuit, no retry) — the C2 principle.
//   - connectivityController: the app-wide tiers the registry deliberately
//     avoids — Tier 0 debounced offline, and breadth-triggered outage (≥3
//     DISTINCT failing keys). Only NO response, or breadth, can be app-wide.
//
// The legacy names below are kept as thin shims so the existing suite (86 files
// call `resetCircuitBreaker`; setup.ts disables the breaker under test) and
// `serverErrorRouting`'s cancel classifier keep working unchanged.

interface LegacyBreakerConfig {
  enabled?: boolean;
  failureThreshold?: number;
  /** Legacy no-op: the half-open probe replaced the blind auto-reset timer. */
  resetTimeoutMs?: number;
}

/**
 * Enable/disable the resilience layer. Tests disable it (`{ enabled: false }`,
 * src/test/setup.ts) because it is module-global singleton state that would
 * otherwise let a fire-and-forget request from one file trip a circuit mid-way
 * through another. One switch governs BOTH the per-endpoint registry and the
 * connectivity controller — they are always on together in production. The
 * breaker's own tests re-enable it explicitly.
 */
export function configureCircuitBreaker(overrides: LegacyBreakerConfig) {
  const registryOverrides: Parameters<typeof configureCircuitRegistry>[0] = {};
  if (overrides.enabled !== undefined) registryOverrides.enabled = overrides.enabled;
  if (overrides.failureThreshold !== undefined) {
    registryOverrides.failureThreshold = overrides.failureThreshold;
  }
  configureCircuitRegistry(registryOverrides);
  if (overrides.enabled !== undefined) configureConnectivity({ enabled: overrides.enabled });
}

/**
 * C5 §7 — per-domain request timeout. The global 120s default is far too long
 * for the consume file-accept POST: a learner on a phone must not sit on the
 * spinner for two minutes.
 */
const CONSUME_WRITE_TIMEOUT_MS = 12000;
/**
 * vingreact#989: the consume STATE PUT waits longer than both edges in front
 * of the API (CloudFront origin read timeout 30 s ⇒ 504; ALB idle 60 s), so the
 * edge answers before the client gives up. At 12 s the client abandoned saves
 * the server was still running and re-sent them behind themselves (09-29).
 */
const CONSUME_STATE_PUT_TIMEOUT_MS = 65000;
/** The global default; long enough for reports/exports, too long for autosave. */
export const DEFAULT_TIMEOUT_MS = 120000;

/**
 * The endpoint pattern a consume write hit, for the alert's `endpoint` field.
 *
 * This used to be the hardcoded string '/api/view/shares/:stub/' on EVERY
 * consume-write report, which is how prod ended up with 57 × 409 that named no
 * caller (#100). The stub is normalized away (it is per-share, so a raw URL
 * would fragment the alert) but the CALLER is not: the state PUT and the
 * file-accept POST are different code paths and must be distinguishable in the
 * alert without opening a trace.
 */
export function consumeWriteEndpoint(url: string): string {
  const path = url.split('?')[0] ?? url;
  const normalized = path.replace(/\/api\/view\/shares\/[^/]+/, '/api/view/shares/:stub');
  return normalized.endsWith('/') ? normalized : `${normalized}/`;
}

function isConsumeWrite(method: string, url: string): boolean {
  if (!url.includes('/api/view/shares/')) return false;
  if (method === 'PUT') return true;
  // The synchronous file-accept step (plan A1.2) is a consume write in every
  // sense that matters here: the learner is waiting on it, so it gets the
  // fail-fast timeout and the consume alert. Retry is NOT decided here (#1186):
  // whether a write may be repeated comes from the table in retryPolicy.ts.
  return method === 'POST' && url.includes('/files/accept/');
}

/**
 * Per-domain request timeout policy (C5 §7, #989): the consume state PUT waits
 * past the edges (65 s), the other consume writes fail fast (12 s), every other
 * route keeps the long default. The single source for the axios interceptor
 * and `keepaliveRequest`. Exported pure so the policy is unit testable without
 * exercising a real timeout.
 */
export function resolveRequestTimeout(method: string, url: string): number {
  if (isConsumeStatePut(method, url)) return CONSUME_STATE_PUT_TIMEOUT_MS;
  return isConsumeWrite(method, url) ? CONSUME_WRITE_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
}

export function calculateBackoff(attempt: number): number {
  const delay = Math.min(
    RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt),
    RETRY_CONFIG.maxDelayMs
  );
  // Bug-hunt #5: previous formula `Math.random() * 2 - 1` could subtract jitter,
  // making retries fire SOONER than baseDelay. Jitter is now non-negative (0..25%).
  const jitter = delay * 0.25 * Math.random();
  return Math.round(delay + jitter);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * `'merged'`: the server said `account_merged_retry` on a proved route (#1336):
 * resend now, once, outside the 5xx budget. `'budget'`: a transport/5xx retry
 * after a backoff. `false`: the error goes to the caller.
 */
type RetryKind = 'merged' | 'budget';

function shouldRetry(error: AxiosError, attempt: number): RetryKind | false {
  // Don't retry if request was cancelled
  if (axios.isCancel(error)) return false;

  const method = (error.config?.method ?? 'GET').toUpperCase();
  const url = error.config?.url ?? '';

  // #1336: the merged resend is decided before the budget (tests and prod with
  // maxRetries 0 still resend it) and before the circuit (a half-open probe
  // that lost the race must not be cancelled). One resend, proved routes only.
  if (isMergedRetryable(error)) return 'merged';

  // Don't retry if we've exceeded max retries
  if (attempt >= RETRY_CONFIG.maxRetries) return false;

  // Don't retry if THIS endpoint's circuit is open (per-endpoint, not global).
  if (isEndpointCircuitOpen(method, url)) return false;

  // #989: a consume state PUT that timed out or got a slow 504 may still be
  // running on the server; a transport retry would queue a second copy behind
  // it. The persist hook owns what happens next. A fast 504 retries below.
  if (isSlowConsumeFailure(error)) return false;

  const status = error.response?.status;

  // #1186 (owner Q-297 b): a write is repeated only when the retry table
  // (retryPolicy.ts) names its route; every other write is sent once, so the
  // person sees the server's error at once instead of after silent repeats.
  // A table route keeps the consume recovery policy: no response, or any
  // retryable status (5xx, 408, 429).
  if (!isReadMethod(method)) {
    if (!idempotentWriteRoute(method, url)) return false;
    return !status || RETRY_CONFIG.retryableStatuses.includes(status) ? 'budget' : false;
  }

  // Reads: a response-bearing 500/502/503/504 only. A read with no response is
  // the TanStack transport retry's (#774), and 408/429 are not repeated.
  if (!status) return false;
  if (!RETRY_CONFIG.retryableStatuses.includes(status)) return false;
  if ([408, 429].includes(status)) return false;
  return 'budget';
}

/** A 409 `account_merged_retry` on a route and request shape the table proves, not yet resent. */
function isMergedRetryable(error: AxiosError): boolean {
  const config = error.config as (InternalAxiosRequestConfig & RetryConfig) | undefined;
  if (!config || config.__mergedRetried || !isAccountMergedRetry(error)) return false;
  const code = SERVER_RETRY_CODES[ACCOUNT_MERGED_RETRY];
  if (!code || code.times < 1) return false;
  const request = { body: parseRetryBody(config.data), authenticated: !!config.headers?.Authorization };
  return !!serverRetryRoute(ACCOUNT_MERGED_RETRY, config.method ?? 'GET', config.url ?? '', request);
}

// Custom error class to track retry state
interface RetryConfig extends AttemptStamped {
  __retryCount?: number;
  /** #1336: the one `account_merged_retry` resend has been sent. */
  __mergedRetried?: boolean;
  __startTime?: number;
  traceOrigin?: string;
  /**
   * #997: this request was admitted as its key's half-open probe. The stamp
   * follows retries and merged resends (they clone the config) to the terminal
   * exit, where `settleProbe` releases the key.
   */
  __circuitProbe?: boolean;
}

/**
 * #997: settle the half-open probe on a terminal exit of the response
 * interceptor. Only a stamped config settles anything: the circuit's own
 * rejection of a blocked request carries no stamp, so it can never free the
 * probe slot. Call it before EVERY return that is not a resend
 * (probeSettlesEveryExit997.guard.test.ts).
 */
function settleProbe(
  config: { method?: string; url?: string; __circuitProbe?: boolean } | undefined,
  outcome: 'success' | 'failure',
): void {
  if (!config?.__circuitProbe) return;
  releaseProbe(config.method?.toUpperCase() || '?', config.url || '?', outcome);
}

function sanitizedConsumePayload(config: InternalAxiosRequestConfig | undefined) {
  try {
    const body = typeof config?.data === 'string' ? JSON.parse(config.data) : config?.data;
    if (!body || typeof body !== 'object') return {};
    const record = body as Record<string, unknown>;
    return {
      componentId: record.component_id ?? record.componentId,
      fields: Object.keys(record).filter(key => !['component_id', 'componentId'].includes(key)),
    };
  } catch {
    return {};
  }
}


// =============================================================================
// AUTH FAILURE HOOK + IDEMPOTENT LOGOUT
// =============================================================================
// Bug-hunt #4, #8: previously a 401 called `useAuthStore.logout()` and then
// `window.location.href = '/login/signIn'` inline. Two problems:
//   - Concurrent 401s (very common when a token expires and several queries are
//     in flight) each fired their own logout + redirect, scattering query state
//     and racing the navigation.
//   - There was no hook for clearing the TanStack queryClient cache, so the
//     next user logging in on the same tab could flash the previous user's
//     data on first paint.
// The new design centralizes both: `handleAuthFailure` is called once per
// session (a flag guards re-entry), invokes any registered hooks (e.g. main.tsx
// registers `queryClient.clear()`), then issues a single redirect.

type AuthFailureHook = () => void;
type ContextRequiredHook = () => void;

const authFailureHooks: AuthFailureHook[] = [];
let authFailureInFlight = false;

// Bug-hunt #87: when backend rolls out 412 enforcement for the
// context-required middleware, this hook lets the app reopen the
// ForcedContextSelectionModal. The handler is fire-and-forget; the
// underlying request still rejects so the caller's catch path runs.
const contextRequiredHooks: ContextRequiredHook[] = [];

export function registerContextRequiredHook(hook: ContextRequiredHook): () => void {
  contextRequiredHooks.push(hook);
  return () => {
    const i = contextRequiredHooks.indexOf(hook);
    if (i >= 0) contextRequiredHooks.splice(i, 1);
  };
}

function fireContextRequired(): void {
  for (const hook of contextRequiredHooks) {
    try {
      hook();
    } catch (err) {
      console.error('[context-required-hook] threw', err);
    }
  }
}

/** Register a callback to run exactly once when the API observes a 401. */
export function registerAuthFailureHook(hook: AuthFailureHook): () => void {
  authFailureHooks.push(hook);
  return () => {
    const i = authFailureHooks.indexOf(hook);
    if (i >= 0) authFailureHooks.splice(i, 1);
  };
}

/** Reset state (test-only). */
export function resetAuthFailureState(): void {
  authFailureInFlight = false;
}

export function handleAuthFailure(redirect: () => void): void {
  if (authFailureInFlight) return;
  authFailureInFlight = true;

  // Run hooks first so cache is cleared before the next page loads.
  for (const hook of authFailureHooks) {
    try {
      hook();
    } catch (err) {
      console.error('[auth-failure-hook] threw', err);
    }
  }

  try {
    clearToken();
    clearCurrentUser();
  } finally {
    redirect();
  }
}

// =============================================================================
// SPA-FALLBACK GUARD
// =============================================================================
// When the API host is misconfigured — a proxy/upstream outage, or a staging
// env whose API base resolves to the static site — `/api/*` requests are
// answered with the SPA's built `index.html`: HTTP 200, `Content-Type:
// text/html`. axios does NOT parse a text/html body (and a JSON body that
// fails to parse is left untouched too), so `response.data` arrives as a raw
// HTML string. `convertKeysToCamel` passes a top-level string through, so the
// HTML masquerades as a valid JSON payload. Most dangerously, `login()` then
// destructures `authToken` as `undefined` off the string and the app marches
// into a tokenless "authenticated" state on ANY password. We catch the HTML
// at the transport boundary and reject it as a typed error so every endpoint —
// login, /auth/me, config, the lot — fails loudly instead of silently.

/**
 * Raised when an apiClient request receives an HTML document where the JSON API
 * contract was expected (the SPA fallback was served for an `/api/*` route).
 */
export class HtmlResponseError extends Error {
  constructor(public readonly url: string, public readonly status?: number) {
    super(
      `Expected JSON from "${url}" but received an HTML document` +
        (status ? ` (HTTP ${status})` : '') +
        '. The API host is likely misconfigured ' +
        '(the SPA fallback was served for an API route).'
    );
    this.name = 'HtmlResponseError';
  }
}

/**
 * True when a response body is the SPA fallback HTML rather than JSON. Checks
 * the content-type header AND a doctype/`<html` prefix on string bodies — a
 * mislabeled `application/json` HTML body fails JSON.parse and is left a string
 * too, so the header check alone isn't sufficient.
 */
export function responseIsHtmlFallback(data: unknown, contentType?: unknown): boolean {
  if (typeof contentType === 'string' && contentType.toLowerCase().includes('text/html')) {
    return true;
  }
  if (typeof data === 'string') {
    const head = data.slice(0, 512).trimStart().toLowerCase();
    return head.startsWith('<!doctype html') || head.startsWith('<html');
  }
  return false;
}

// Endpoints that don't require authentication
const NO_AUTH_ENDPOINTS = [
  'POST:/api/auth/login/',
  'POST:/api/auth/register/',
  'POST:/api/auth/generate-link/',
  'POST:/api/auth/password/reset/',
  'POST:/api/auth/password/reset/confirm/',
  'POST:/api/auth/link/exchange/', // magic-link exchange: the token is in the RESPONSE (#372)
  'POST:/api/shorturl/claim/', // claim exchange: identity rides on ving_sid, never a token (#372)
];

// Endpoints where auth is optional (works with or without token)
const OPTIONAL_AUTH_ENDPOINTS = [
  'GET:/api/view/shares/',
  'PUT:/api/view/shares/',
  'POST:/api/auth/verify/', // Can verify with token OR verification_key
  'GET:/api/branding/', // Public white-label resolver; called pre-auth on login pages
];

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS, // 2 minutes default (consume writes shortened per-request)
  headers: {
    'Content-Type': 'application/json',
  },
  // Serialize arrays as repeated params: filters=a&filters=b (Django expects this)
  // NOT bracket notation: filters[]=a&filters[]=b (axios default)
  paramsSerializer: {
    indexes: null, // Use repeated keys instead of indexed brackets
  },
});

/**
 * The `Authorization` value for the stored session token, or undefined when
 * there is none (signed out, or cookie-mode). The one place that shapes it: the
 * request interceptor and the keepalive transport both read it here.
 */
function storedAuthorization(): string | undefined {
  const token = getToken();
  return token ? `Token ${token}` : undefined;
}

/**
 * vingreact#1332: the trace-id (from the `traceparent`) of the request that produced a response
 * body, so a client error report CAUSED by that request carries its id
 * (spec 1332-a). A side table keyed by the returned body: `updateViewState`
 * resolves a bare `ViewResponse`, and its return type stays that.
 */
const causeTraceIds = new WeakMap<object, string>();

function stampCauseTrace<T>(body: T, traceId: unknown): T {
  if (typeof body === 'object' && body !== null && typeof traceId === 'string' && traceId) {
    causeTraceIds.set(body, traceId);
  }
  return body;
}

/** The trace id of the request whose reply is `response`; undefined if unknown. */
export function causeTraceIdOf(response: unknown): string | undefined {
  return typeof response === 'object' && response !== null ? causeTraceIds.get(response) : undefined;
}

/** Stamp an axios response's body with the id its request carried. */
export function stampAxiosCauseTrace<T>(response: AxiosResponse<T>): T {
  return stampCauseTrace(response.data, requestTraceId(response.config));
}

/** A request for {@link keepaliveRequest}: the same fields the axios call takes. */
export interface KeepaliveRequest {
  method: 'PUT' | 'POST';
  url: string;
  data: Record<string, unknown>;
  params?: Record<string, string>;
  /**
   * Caller's abort (vingreact#989 U3: the reconnect path aborts a hung consume
   * PUT). Aborting rejects with an axios `CanceledError`, never a timeout.
   */
  signal?: AbortSignal;
}

/**
 * Send one request through `fetch(..., { keepalive: true })` — the transport
 * that survives page unload, which an axios XHR does not (vingreact#715: the
 * departure flush of a reader who dwelt then closed the tab).
 *
 * Same wire as `apiClient`: `baseURL` and the axios params serializer (via
 * `apiClient.getUri`), the snake-cased body and query (`convertKeysToSnake`,
 * exactly as the request interceptor), the stored-token `Authorization`, JSON
 * content type, and `credentials: 'include'` so the `ving_sid` session cookie
 * rides along. Resolves to the camelCased body on 2xx; otherwise rejects with
 * an `AxiosError` carrying `response.status` / raw `response.data` (the shape
 * the interceptor path rejects with, so the consume classifiers apply
 * unchanged), or `ERR_NETWORK` with no response when fetch itself rejects.
 *
 * NOT async on purpose: a SYNCHRONOUS throw from `fetch` (unsupported, or a
 * body the engine refuses up front) propagates to the caller synchronously so
 * it can fall back to the default transport. None of the axios interceptors'
 * side effects run (only an answer's proof of life reaches the connectivity
 * controller, #1843) — this is for a page that is leaving.
 *
 * Timeout (#989): the same `resolveRequestTimeout` as the axios path (65 s for
 * the consume state PUT, 120 s default elsewhere) through an AbortController.
 * On expiry it rejects like an axios timeout: an `AxiosError` with code
 * `ECONNABORTED`, "timeout of N ms exceeded", and a config that carries no
 * aborted signal — so `classifyFailure` reads `transport_timeout`, not an
 * unload cancel. The timer is cleared when the request settles.
 */
export function keepaliveRequest<T>(req: KeepaliveRequest): Promise<T> {
  const params = req.params ? convertKeysToSnake(req.params) : undefined;
  const url = apiClient.getUri({ url: req.url, params });
  // #1332: a keepalive write is traceable server-side too, and its reply is
  // stamped with this id (causeTraceIdOf). Headers are not in the budgeted body.
  const traceId = newTraceId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    traceparent: buildTraceparent(traceId, newSpanId()),
    baggage: buildBaggage(),
  };
  const auth = storedAuthorization();
  if (auth) headers.Authorization = auth;
  // The one body string (vingreact#989 S5b K4): `keepaliveBodyBytes` measures
  // exactly this, so the budget check and the wire agree byte for byte.
  const body = keepaliveBody(req.data);
  const timeoutMs = resolveRequestTimeout(req.method, req.url);
  const config = {
    url,
    method: req.method.toLowerCase(),
    headers: new AxiosHeaders(headers),
    data: body,
  } as InternalAxiosRequestConfig & AttemptStamped;

  const cancelError = () => {
    const error = new axios.CanceledError('canceled');
    error.config = config;
    return error;
  };
  const callerAborted = () => req.signal?.aborted === true;
  const sent = fetchWithTimeout(
    url,
    {
      method: req.method,
      headers,
      body,
      credentials: 'include',
      // vingreact#1457: Chromium <= 80 refuses keepalive + a preflight header.
      ...(keepaliveAllowsPreflight() ? { keepalive: true } : {}),
    },
    { timeoutMs, signal: req.signal },
  );
  // Armed only once fetch accepted the request (a synchronous throw above
  // leaves no timer behind and charges nothing).
  config.__attemptStart = Date.now();
  // vingreact#989 S5b K4: the body's bytes count against the page's keepalive
  // quota until the request settles (charged below, once the settle handler
  // is attached).
  let releaseBytes: () => void = () => undefined;
  const timeoutError = () => new axios.AxiosError(
    `timeout of ${timeoutMs}ms exceeded`,
    axios.AxiosError.ECONNABORTED,
    config,
    null,
  );
  /**
   * The body read shares the one limit (what is left of it after the headers
   * arrived) and honours the caller's abort: fetchWithTimeout has already
   * dropped its listeners by then. Timer and listener are removed on every path.
   */
  const readText = (res: Response): Promise<string> => {
    const remaining = Math.max(0, timeoutMs - (Date.now() - config.__attemptStart!));
    let timer: ReturnType<typeof setTimeout> | undefined;
    let onAbort: (() => void) | undefined;
    const interrupted = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new FetchTimeoutError(timeoutMs)), remaining);
      if (req.signal?.aborted) reject(new DOMException('aborted', 'AbortError'));
      else {
        onAbort = () => reject(new DOMException('aborted', 'AbortError'));
        req.signal?.addEventListener('abort', onAbort, { once: true });
      }
    });
    return Promise.race([res.text(), interrupted]).finally(() => {
      clearTimeout(timer);
      if (onAbort) req.signal?.removeEventListener('abort', onAbort);
    });
  };

  const settled = sent.then(
    async (res) => {
      let text: string;
      try {
        text = await readText(res);
      } catch (err) {
        if (callerAborted()) throw cancelError();
        if (err instanceof FetchTimeoutError) throw timeoutError();
        throw err;
      }
      let data: unknown = text;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          // Left as the raw string: an HTML fallback is caught below, any
          // other non-JSON body is what the server sent.
        }
      }
      const contentType = res.headers.get('content-type') ?? undefined;
      // #1843: an answer from the server (2xx/4xx) is proof of life, as it is on
      // the axios path: it cancels a pending countdown to the outage screen.
      // 5xx and HTML fallbacks stay out (the leave path feeds no breadth).
      const reportAlive = () => noteApiResult({
        method: req.method,
        url: req.url,
        status: res.status,
        hadResponse: true,
        dateHeader: res.headers.get('date'),
      });
      if (res.ok) {
        if (responseIsHtmlFallback(data, contentType)) {
          throw new HtmlResponseError(url, res.status);
        }
        reportAlive();
        return stampCauseTrace(convertKeysToCamel(data) as T, traceId);
      }
      if (res.status < 500) reportAlive();
      const response: AxiosResponse = {
        data,
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        config,
      };
      throw new axios.AxiosError(
        `Request failed with status code ${res.status}`,
        res.status >= 500 ? axios.AxiosError.ERR_BAD_RESPONSE : axios.AxiosError.ERR_BAD_REQUEST,
        config,
        null,
        response,
      );
    },
    (err: unknown) => {
      if (callerAborted()) throw cancelError();
      if (err instanceof FetchTimeoutError) throw timeoutError();
      throw new axios.AxiosError(
        err instanceof Error ? err.message : 'Network Error',
        axios.AxiosError.ERR_NETWORK,
        config,
        null,
      );
    },
  ).finally(() => {
    releaseBytes();
  });
  releaseBytes = chargeKeepaliveBytes(utf8Bytes(body));
  return settled;
}

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig & RetryConfig) => {
    const method = config.method?.toUpperCase() || 'GET';
    const url = config.url || '';
    const fullEndpoint = `${method}:${url}`;
    const retryCount = config.__retryCount || 0;
    // #1336: a merged resend is a retry too (it bypasses the circuit gate like a
    // budget retry), and counts in X-Retry-Attempt.
    const resends = retryCount + (config.__mergedRetried ? 1 : 0);

    // C5 Tier 1: short-circuit ONLY this endpoint's key when its circuit is open
    // (a retry already in flight bypasses the gate). `admitRequest` also
    // admits a single half-open probe once the backoff has elapsed. The Cancel
    // message is preserved verbatim — serverErrorRouting classifies it.
    if (resends === 0) {
      const admission = admitRequest(method, url);
      if (admission === 'blocked') {
        console.warn(`[API] ${method} ${url} BLOCKED - endpoint circuit is open`);
        return Promise.reject(new axios.Cancel('Circuit breaker is open'));
      }
      // #997: the probe is stamped; its terminal exit releases the key.
      if (admission === 'probe') config.__circuitProbe = true;
    }

    // C5 §7: consume writes fail fast (see resolveRequestTimeout). Only narrowed
    // for consume writes so any caller-specified timeout elsewhere is preserved.
    if (isConsumeWrite(method, url)) {
      config.timeout = resolveRequestTimeout(method, url);
    }

    // Convert request data to snake_case
    if (config.data && typeof config.data === 'object') {
      config.data = convertKeysToSnake(config.data);
    }

    // Convert query params to snake_case
    if (config.params && typeof config.params === 'object') {
      config.params = convertKeysToSnake(config.params);
    }

    // Add auth token unless endpoint is in no-auth list
    const isNoAuth = NO_AUTH_ENDPOINTS.some((ep) => fullEndpoint.startsWith(ep));
    const isOptionalAuth = OPTIONAL_AUTH_ENDPOINTS.some((ep) =>
      fullEndpoint.startsWith(ep)
    );

    if (!isNoAuth) {
      const storedAuth = storedAuthorization();
      if (config.headers.Authorization) {
        // An EXPLICIT Authorization set by the caller wins over the stored
        // token. The two callers that set one — `exchangeToken` (/sso magic
        // link, SignIn after a password sign-in) and `getMe(authToken)` — hold a
        // token the person is not signed in with YET. Overwriting it with the
        // stale stored token exchanged the wrong credential: the fresh token
        // was never redeemed and the arrival 401'd (#268 follow-up).
      } else if (storedAuth) {
        config.headers.Authorization = storedAuth;
      } else if (!isOptionalAuth) {
        // Token required but not present and not provided in request - will likely fail
        console.warn('No auth token available for protected endpoint:', url);
      }
    }

    // R7 (#239c): stamp the start time for elapsed-ms on slow reports.
    if (!config.__startTime) {
      config.__startTime = Date.now();
    }
    // #989: THIS attempt's start, re-stamped on every retry (a retry clones the
    // config, so `__startTime` above keeps the first attempt's). The slow-504
    // rule measures the attempt that got the 504, never the whole request.
    config.__attemptStart = Date.now();

    // F2: trace ID must be STABLE across retries so server-side log
    // correlation works. Only generate a new ID on the first attempt.
    // W3C traceparent carries it (vingreact#1104): the first attempt mints the
    // trace-id, a retry keeps it and gets a fresh parent-id.
    config.headers['traceparent'] = buildTraceparent(requestTraceId(config) ?? newTraceId(), newSpanId());
    config.headers['baggage'] = buildBaggage();
    if (resends > 0) {
      config.headers['X-Retry-Attempt'] = String(resends);
    }
    // vingreact#717 (1123b): the page path may carry the recipient-link
    // segment (`/view/<stub>/<link>/`); it never leaves in a header.
    // A request interceptor must never throw: a page path that is not a string
    // (a stubbed location) passes through untouched, as before.
    const pagePath: unknown = window.location.pathname;
    config.headers['X-Trace-Origin'] = config.traceOrigin
      || (typeof pagePath === 'string' ? stripLinkSegment(pagePath) : pagePath);

    // Read-your-writes: a GET inside the post-write window declares itself so
    // the API serves it from the writer, not a lagging replica
    // (src/api/readYourWrites.ts). Mutations pin themselves server-side.
    if (!isMutatingMethod(method) && shouldDeclareReadYourWrites()) {
      config.headers[READ_YOUR_WRITES_HEADER] = '1';
    }

    // Staff org override - allows staff to view app as different organization
    const user = getCurrentUser();
    const orgOverride = useStaffStore.getState().orgOverride;
    if (user?.isStaff && orgOverride) {
      config.headers['X-Staff-Org-Override'] = orgOverride.id.toString();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // SPA-fallback guard: an HTML body where JSON was expected means the API
    // host is misconfigured. Treat it as a transport FAILURE — not a success —
    // so it can't masquerade as a valid contract (e.g. a tokenless "login").
    // recordEndpointFailure() lets that endpoint's circuit trip after repeated
    // fallbacks, stopping the request storm a blank authenticated shell would
    // otherwise fire off.
    const reqMethod = response.config?.method?.toUpperCase() || '?';
    const reqUrl = response.config?.url || '?';
    const dateHeader = response.headers?.['date'] as string | undefined;

    // #1076: axios `settle` resolves a status-0 response (Firefox aborts in-flight
    // XHRs on a download click and ends them with `loadend` only). That is never
    // a success: reject as a browser abort (class `cancel`: no connectivity feed,
    // no circuit count, no retry, no crash report).
    if (!response.status) {
      settleProbe(response.config as RetryConfig | undefined, 'failure');
      addBreadcrumb('api', `${reqMethod} ${reqUrl} → aborted (status 0)`, {
        status: 0,
        traceId: requestTraceId(response.config),
      });
      return Promise.reject(
        new BrowserAbortError(response.config, response.request),
      );
    }

    if (responseIsHtmlFallback(response.data, response.headers?.['content-type'])) {
      // A persistent HTML fallback is that endpoint's own failure — open its
      // circuit after repeated ones (Tier 1). A misconfig hitting many routes
      // also lights up breadth (treatAsServerError), so the app-wide boundary
      // can catch it. It is still a RESPONSE, so it never drives Tier-0 offline.
      recordEndpointFailure(reqMethod, reqUrl);
      settleProbe(response.config as RetryConfig | undefined, 'failure');
      noteApiResult({
        method: reqMethod,
        url: reqUrl,
        status: response.status,
        hadResponse: true,
        treatAsServerError: true,
        dateHeader,
      });
      console.error(
        `[API] ${reqMethod} ${reqUrl} returned an HTML document where JSON was ` +
          `expected (HTTP ${response.status}) — API host misconfigured`
      );
      addBreadcrumb('api', `${reqMethod} ${reqUrl} → HTML fallback (${response.status})`, {
        status: response.status,
        traceId: requestTraceId(response.config),
      });
      return Promise.reject(new HtmlResponseError(reqUrl, response.status));
    }

    // Success - any success on the key resets its circuit + heals connectivity.
    recordEndpointSuccess(reqMethod, reqUrl);
    settleProbe(response.config as RetryConfig | undefined, 'success');
    // A 2xx mutation opens the read-your-writes window for the GETs that follow.
    if (isMutatingMethod(reqMethod)) {
      noteWrite();
    }
    noteApiResult({ method: reqMethod, url: reqUrl, status: response.status, hadResponse: true, dateHeader });

    // Breadcrumb: record the call for crash-report context. The trace ID
    // cross-links to server logs / the admin trace viewer.
    addBreadcrumb('api', `${reqMethod} ${reqUrl} → ${response.status}`, {
      status: response.status,
      traceId: requestTraceId(response.config),
    });

    // Convert response data to camelCase, honoring any request-scoped opaque
    // keys (subtrees that must not be case-converted for this request).
    if (response.data) {
      const opaqueKeys = response.config?.opaqueKeys;
      response.data = convertKeysToCamel(
        response.data,
        opaqueKeys ? new Set(opaqueKeys) : undefined,
      );
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & RetryConfig) | undefined;
    const method = config?.method?.toUpperCase() || '?';
    const url = config?.url || '?';
    const status = error.response?.status;
    const retryCount = config?.__retryCount || 0;

    console.error(`[API] ${method} ${url} failed with ${status}:`, error.message);

    // Breadcrumb: record the failed call so a later crash report shows
    // it. The trace ID is the bridge to the server-side trace.
    addBreadcrumb('api', `${method} ${url} → ${status ?? 'network error'}`, {
      status: status ?? null,
      traceId: requestTraceId(config),
    });

    // F3: 401s from OPTIONAL_AUTH_ENDPOINTS must NOT trigger logout.
    if (status === 401) {
      // The explicit token-exchange call sites own their own UX and reporting
      // (#268): bouncing them unloads the page before the inline error can
      // render — the mechanism behind "blank login screen after a link". A
      // flagged request gets NO side effects here.
      if (config?.skipAuthRedirect) {
        settleProbe(config, 'failure');
        return Promise.reject(error);
      }
      const fullEndpoint = `${method}:${url}`;
      const isOptionalAuth = OPTIONAL_AUTH_ENDPOINTS.some((ep) =>
        fullEndpoint.startsWith(ep)
      );
      // A no-auth endpoint (login, register, link request, password reset)
      // never carried a session token, so its 401 is a verdict on the
      // credentials in the body — not a dead session. Bouncing it would log
      // the person out of nothing and, with #268, file a bogus
      // "session rejected" report for a wrong password.
      const isNoAuth = NO_AUTH_ENDPOINTS.some((ep) => fullEndpoint.startsWith(ep));
      if (!isOptionalAuth && !isNoAuth) {
        // W7: the login page explains the bounce. An ordinary session end is
        // expected and not reported; a rejection is a funnel failure worth an
        // alert.
        const detail = readDetail(error);
        const errorCode = readDetailError(error);
        const reason = classifyBounceReason(detail, errorCode);
        // The once-guard: a token expiring with several queries in flight
        // produces a burst of 401s, and ops needs ONE alert, not one per query.
        if (!authFailureInFlight && reason === 'session_rejected') {
          // Report BEFORE handleAuthFailure — it clears the token, so
          // `hadToken` must be read while the rejected token is still there.
          reportClientError({
            error: authFailureError('session_rejected', detail ?? '401'),
            source: 'auth',
            traceId: traceIdFromError(error),
            // handleAuthFailure navigates on the next line, which kills the
            // keepalive fetch's promise AFTER the server has taken the POST —
            // the reporter's `.catch` fallback then beacons the same payload
            // and every session_rejected count reads double. Beacon once.
            transport: 'beacon',
            context: {
              stage: 'session_rejected',
              method,
              url,
              status: 401,
              detail: detail ?? null,
              hadToken: Boolean(getToken()),
            },
          });
        }
        handleAuthFailure(() => {
          // #1111 (1111b): on a course page the bounce carries it as the
          // sign-in redirect; elsewhere the URL is `/login/signIn?reason=…`.
          // `?? ''`: a stubbed Location (tests) may carry no `search` accessor.
          const here = `${window.location.pathname}${window.location.search ?? ''}`;
          window.location.href = authRouteHref(paths.loginSignIn, viewReturnAddress(here), { params: { reason } });
        });
      }
      settleProbe(config, 'failure');
      return Promise.reject(error);
    }

    // Bug-hunt #87: 412 Precondition Required signals the backend's
    // "context selection required" middleware (uptake #3845). Fire the
    // registered hook (the app's ForcedContextSelectionModal re-opens)
    // and surface the rejection so the caller can fall back.
    if (status === 412) {
      fireContextRequired();
      settleProbe(config, 'failure');
      return Promise.reject(error);
    }

    // 403s may carry a merge suggestion (vingapi uptake #4216): the user's
    // strongest user-merge whose OTHER account holds the denied access. Push
    // it to the store driving the app-wide AccountMergeHost and STILL reject —
    // feature-level error handling is unchanged; the dialog overlays it.
    // parseDenialEnvelope fails closed on plain {detail} bodies and malformed
    // envelopes.
    //
    // /api/view/ (consume) used to be excluded, on the grounds that the area
    // has its own denial pipeline and the backend never attached suggestions
    // there. The second half stopped being true: the consume gate now records
    // and delivers a CONTENT-grant suggestion when a contested representation
    // explains the denial (vingapi 45d8cf57 + follow-up). Excluding it would
    // drop the one signal that unblocks the page the viewer is looking at.
    // AccountMergeHost still suppresses the PROACTIVE channel inside /view —
    // that decision was about not interrupting viewers, which a denial the
    // user is already stuck on does not do.
    if (status === 403) {
      const envelope = parseDenialEnvelope(error.response?.data);
      if (envelope) {
        useMergeSuggestionStore.getState().showDenial(envelope);
      }
    }

    // Handle cancelled requests (don't retry, don't record failure)
    if (axios.isCancel(error)) {
      settleProbe(config, 'failure');
      return Promise.reject(error);
    }

    // Consume-write alerting: a FAILED CONSUME WRITE IS ALWAYS ALERT-WORTHY. We
    // expect consume writes to succeed and expect the client NOT to write to a
    // closed/denied assignment, so any failed consume PUT — including a terminal
    // 403 access-denied, 409 review-mode, or 422 validation — is an UNEXPECTED
    // client-misbehavior signal that must surface. Alert on EVERY failed attempt,
    // including ones that will be retried silently (the retried request re-enters
    // this handler). This is decoupled from the USER-facing message (reconnect vs.
    // the server's real reason), which the classifier/save banner decide separately.
    //
    // #100 NOTE — do NOT gate this on the known-refusal registry. The alert IS the
    // instrument that surfaces a client firing a doomed write; a recognisable
    // refusal code makes the alert MORE actionable, not less alert-worthy. The
    // 409 flood (condition 19) is fixed at the EMITTER — the viewer must not fire
    // the write — which is what `identifyRefusal` is NOT for. The registry routes
    // UX and gates the non-consume (admin form) telemetry only.
    //
    // The diagnostic enrichment below is the other half of that fix: `endpoint` is
    // derived per caller (the state PUT vs the file-accept POST) instead of a
    // hardcoded string, and the refusal `code` rides along, so the next flood
    // names the caller and the reason without a trace lookup.
    // E4: this lives BELOW the isCancel early-return, so an aborted (benign) save
    // can never false-alert; 401/412 already returned above (their own UX flows).
    // `recoverable` is the classifier's retry verdict. Never attach the request
    // body: it may contain learner answers.
    // Compute willRetry BEFORE the report so transport dims are accurate.
    const retryKind = config ? shouldRetry(error, retryCount) : false;
    const willRetry = !!retryKind;

    // #1336 R3: a merged 409 that is about to be resent is the race working as
    // designed, not a failed write: no report. The second one is reported like a 500.
    const consumeWriteForReport = isConsumeWrite(method, url) && retryKind !== 'merged';
    if (consumeWriteForReport && !isExpectedConsumeRefusal(error)) {
      reportClientError({
        error,
        source: 'consume',
        traceId: requestTraceId(config) ?? newTraceId(),
        context: {
          attempt: retryCount + 1,
          recoverable: classifyConsumeError(error).retry,
          method,
          endpoint: consumeWriteEndpoint(url),
          // The refusal code when the body carried one. An UNMAPPED code is
          // exactly the case this report exists for, and the code is what makes
          // it triageable without a trace lookup.
          ...(consumeErrorCode(error) ? { code: consumeErrorCode(error) } : {}),
          ...sanitizedConsumePayload(config),
          // Transport dims: exhausted/online for ops grouping (D1). Present on
          // every transport failure report; absent on response-bearing failures
          // so existing consumers see the same shape byte-for-byte.
          ...(isTransportFailure(error) ? transportReportDims({ willRetry }) : {}),
        },
      });
    }

    // Check if we should retry
    if (retryKind === 'merged' && config) {
      // Same request, same trace id, same body; no sleep and no budget spent.
      const mergedConfig = { ...config, __mergedRetried: true };
      return apiClient.request(mergedConfig);
    }
    if (willRetry) {
      const nextRetry = retryCount + 1;
      const backoff = calculateBackoff(retryCount);

      await sleep(backoff);

      // Clone config and increment retry count
      const retryConfig = { ...config, __retryCount: nextRetry };
      return apiClient.request(retryConfig);
    }

    // C5: feed the per-endpoint circuit (Tier 1) + the app-wide controller.
    // Runs only on a TERMINAL failure (a retried request returned above), so the
    // per-endpoint consecutive-5xx count matches the old breaker's semantics.
    //
    // R3 (#239c): an unload abort (visibilityState hidden, signal aborted, or
    // browser-generated abort messages) classifies as 'cancel', not transport.
    // The interceptor must not call noteApiResult for it.
    const errDateHeader = error.response?.headers?.['date'] as string | undefined;
    // #989/#997: an OPEN key admits one half-open probe (stamped on its
    // config) and blocks everything else until that probe settles. Every
    // terminal exit settles it via `settleProbe` (a failed probe re-arms with
    // backoff). On a closed key only a counted failure (5xx, not slow) is recorded.
    if (!error.response) {
      if (classifyFailure(error) === 'cancel') {
        // R3: unload abort — do not feed the connectivity controller.
        settleProbe(config, 'failure');
        return Promise.reject(error);
      }
      // No response — cannot open an endpoint circuit (not response-bearing);
      // this is the ONLY app-wide-capable signal → Tier 0 (offline) + breadth.
      // R2 (#239c): pass wasTimeout so the controller knows timeout vs network.
      // R7 (#239c): pass traceId/elapsed/timeout for slow report.
      noteApiResult({
        method,
        url,
        hadResponse: false,
        wasTimeout: isTransportTimeout(error),
        traceId: requestTraceId(config) ?? '',
        elapsedMs: config?.__startTime ? Date.now() - config.__startTime : undefined,
        timeoutMs: config?.timeout as number | undefined,
      });
    } else {
      // A response of ANY status is proof of life. Only 5xx opens the endpoint
      // circuit; a 4xx (incl. the review-mode 409) gates nothing (C2).
      // #989: a slow consume failure (504 from a slow origin) is not the
      // endpoint failing: it must not open the consume circuit, which every
      // consume PUT in the tab shares. A fast 504 and real 5xx count.
      // #1336: a terminal merged 409 counts as the 500 it is treated as.
      if ((((status && status >= 500) || isAccountMergedRetry(error)) && !isSlowConsumeFailure(error))) {
        recordEndpointFailure(method, url);
      }
      noteApiResult({ method, url, status, hadResponse: true, dateHeader: errDateHeader });
    }

    // For 4xx: replace Axios generic message with actual API error message.
    // This makes error.message useful everywhere it's consumed (toast handlers,
    // ProfileEditDialog, AccountMergeDialog, etc.) without changing call sites.
    // #1336 R4: not the merged 409's own message: other surfaces print the
    // central server-error copy.
    if (status && status >= 400 && status < 500 && error.response?.data && !isAccountMergedRetry(error)) {
      const data = error.response.data as Record<string, unknown>;
      const detail = data?.detail;
      if (typeof detail === 'string' && detail.length > 0) {
        error.message = detail;
      } else if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const msg = (detail as Record<string, unknown>).message;
        if (typeof msg === 'string' && msg.length > 0) {
          error.message = msg;
        }
      }
    }

    // 5xx presentation is handled by the QueryClient cache callbacks
    // (src/api/serverErrorRouting.ts), which can tell a screen-breaking
    // failure from an incidental one. The interceptor only does transport.
    settleProbe(config, 'failure');
    return Promise.reject(error);
  }
);

/**
 * Manually reset the whole resilience layer (e.g. a "Retry" button, or per-test
 * setup). Clears every per-endpoint circuit AND the connectivity controller
 * (breadth window, Tier-0 debounce, recovery timers).
 */
export function resetCircuitBreaker(): void {
  resetCircuitRegistry();
  resetConnectivity();
}

/**
 * True when ANY endpoint circuit is open. Kept for the legacy call sites; the
 * per-endpoint truth is `isEndpointCircuitOpen(method, url)`.
 */
export function isApiCircuitOpen(): boolean {
  return anyCircuitOpen();
}

/**
 * Schema for an S3 / S3-compatible (R2, MinIO) presigned-POST response.
 *
 * Bug-hunt #6, #10: previously `data.fields.key` was assumed unconditionally —
 * a non-canonical or partially-populated presign response threw a
 * `TypeError: Cannot read 'key' of undefined` deep inside the upload flow.
 * Parsing at the boundary surfaces a typed `PresignShapeError` instead.
 */
const PresignedPostSchema = z.object({
  url: z.string().url(),
  fields: z.record(z.string(), z.string()).refine(
    (fields) => typeof fields.key === 'string' && fields.key.length > 0,
    { message: 'presigned-post fields must include a non-empty `key`' }
  ),
});

export class PresignShapeError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'PresignShapeError';
  }
}

/**
 * A rejected direct-to-S3 POST, carrying S3's own reason (#84).
 *
 * The S3 presigned-POST leg is a bare `axios.post` outside `apiClient`, so
 * nothing in the interceptor chain sees it. Axios collapses S3's answer to
 * `Request failed with status code 400`; S3's XML body is where the actual
 * reason lives (`RequestTimeout` — the connection stalled mid-body — vs
 * `EntityTooLarge`, `AccessDenied`, or a policy condition). Losing that string
 * cost us a 99.69 MB upload we still cannot classify from prod evidence, so
 * the code is parsed out, carried on a typed error, and reported.
 */
export class S3UploadError extends Error {
  constructor(
    /** S3 `<Code>` — 'Unknown' when the body is missing or not S3 XML. */
    public readonly code: string,
    message: string,
    /** S3 `<RequestId>`, the id AWS support / S3 access logs key on. */
    public readonly requestId: string | undefined,
    /** HTTP status of the rejected POST (0 when there was no response). */
    public readonly status: number,
    /** Bytes the browser had sent when the POST failed. */
    public readonly bytesSent: number,
    public readonly fileSize: number,
    /** The file's MIME type (`file.type`), never its name. */
    public readonly fileType: string,
    /** The file's extension, lowercased, no dot ('' when it has none). */
    public readonly fileExtension: string,
  ) {
    super(message);
    this.name = 'S3UploadError';
  }
}

/** S3 error XML is flat: <Error><Code/><Message/><RequestId/></Error>. */
function s3XmlField(body: string, tag: string): string | undefined {
  const match = new RegExp(`<${tag}>([^<]*)</${tag}>`).exec(body);
  const value = match?.[1]?.trim();
  return value ? value : undefined;
}

/**
 * S3's error body as a string, whatever axios made of it. A presigned POST
 * answers `text/xml`, but a proxy/CDN in front of the bucket can answer HTML
 * or nothing at all — hence the defensive shapes.
 */
function s3ErrorBody(data: unknown): string {
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    try {
      return JSON.stringify(data);
    } catch {
      return '';
    }
  }
  return '';
}

export interface UploadFileToS3Options {
  /**
   * Optional AbortSignal — passing one lets callers cancel both the
   * presign request and the S3 PUT mid-flight (bug-hunt #34, #9).
   * Without this, an unmounted component's upload kept running, the
   * eventual response fired onSuccess into a dead tree (dev-only
   * warning), and bandwidth was wasted.
   */
  signal?: AbortSignal;
  /**
   * Optional byte-level progress callback. Receives an integer percentage
   * (0–100) as the S3 POST uploads. Used by the upload-queue processor —
   * audit M5 consolidated the queue's duplicate (and unvalidated) S3 upload
   * implementation onto this one.
   */
  onProgress?: (percentCompleted: number) => void;
  /**
   * Consume-session share stub. When set, the presigned-post is requested from
   * the no-auth, stub-scoped consume endpoint
   * (`/api/view/shares/{stub}/generate-presigned-post/`) instead of the
   * auth-required global endpoint. Required for anonymous shared-link viewers
   * uploading answer/certificate files — the global endpoint requires
   * CurrentUser and would 401 for them (XCONSUME-003).
   */
  stub?: string;
  /**
   * Which attempt this is (1-based). Rides on the failure report so a stall
   * pattern — "four attempts, all RequestTimeout, all around 3 MB" — is
   * visible in ops without correlating separate alerts (#84). The upload
   * queue passes its retry count; single-shot callers leave it at 1.
   */
  attempt?: number;
}

/**
 * Upload a file directly to S3 using a presigned URL
 */
export async function uploadFileToS3(
  file: File,
  options: UploadFileToS3Options = {}
): Promise<string> {
  // Anonymous consume uploads must use the stub-scoped no-auth endpoint; only
  // the authenticated app uses the global one.
  const presignUrl = options.stub
    ? `/api/view/shares/${encodeURIComponent(options.stub)}/generate-presigned-post/`
    : '/api/files/generate-presigned-post/';
  // Get presigned URL from backend
  const response = await apiClient.get<unknown>(
    presignUrl,
    {
      params: {
        // Raw filename: axios encodes params on the wire. Pre-encoding here
        // double-encodes, so the backend receives the literal "%20"s and
        // bakes them into the S3 key and stored filename.
        filename: file.name,
        type: file.type,
      },
      signal: options.signal,
    }
  );

  const parsed = PresignedPostSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new PresignShapeError(
      'Backend returned an invalid presigned-post response',
      parsed.error
    );
  }
  const data = parsed.data;

  // Upload to S3
  const formData = new FormData();
  Object.entries(data.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  // Bug-hunt #7: previously this passed `Content-Type: multipart/form-data`
  // explicitly. The browser sets this header automatically AND appends the
  // `boundary=` param — overriding it strips the boundary, causing some
  // receivers to reject the upload. Letting axios + the browser handle it
  // produces a single header with the correct boundary.
  // Track bytes actually pushed so a failure report can say WHERE it died —
  // a stall at 3 MB of 100 MB reads very differently from one at 99 MB.
  let bytesSent = 0;

  try {
    await axios.post(data.url, formData, {
      timeout: 7200000, // 2 hours for large files
      signal: options.signal,
      onUploadProgress: (progressEvent) => {
        bytesSent = progressEvent.loaded;
        if (options.onProgress && progressEvent.total) {
          options.onProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }
      },
    });
  } catch (error) {
    // A user/unmount cancellation is not a failure — never report it, never
    // reshape it (the queue processor recognises it by axios's own code).
    if (axios.isCancel(error) || (error instanceof DOMException && error.name === 'AbortError')) {
      throw error;
    }

    const response = (error as AxiosError | undefined)?.response;
    const status = response?.status ?? 0;
    const body = s3ErrorBody(response?.data);
    const code = s3XmlField(body, 'Code') ?? 'Unknown';
    const s3Message = s3XmlField(body, 'Message');
    const requestId = s3XmlField(body, 'RequestId');
    const ext = fileExtension(file.name);

    // The error we report and the error we throw are the SAME object when S3
    // answered: the report's `message` is then S3's reason, not axios's
    // generic status string. A transport failure (no response) keeps axios's
    // own error — its `code` is what the retry classification reads.
    const failure = response
      ? new S3UploadError(
          code,
          // NEVER axios's `Request failed with status code 400` — that string
          // is what made the prod failure unclassifiable. Without an S3
          // <Message> we synthesise one naming the code and status.
          s3Message ?? `Upload rejected by storage (${code}, HTTP ${status})`,
          requestId,
          status,
          bytesSent,
          file.size,
          file.type,
          ext,
        )
      : error;

    // Reporting lives HERE, at the one place both upload paths go through
    // (the upload-queue processor and useS3Upload) — so every rejected S3
    // POST is reported exactly once per attempt, with no per-caller wiring
    // and no double-report. A retried attempt re-enters here and reports
    // again (deliberate: the `attempt` dimension is how a stall pattern
    // becomes visible). Never the filename, never the bytes — extension,
    // MIME type and sizes only.
    reportClientError({
      error: failure,
      source: 'upload',
      context: {
        attempt: options.attempt ?? 1,
        s3Code: code,
        s3Message,
        s3RequestId: requestId,
        status,
        bytesSent,
        fileSize: file.size,
        fileType: file.type,
        fileExtension: ext,
        anonymousConsume: Boolean(options.stub),
      },
    });

    throw failure;
  }

  // Schema refine() guarantees `key` is a non-empty string.
  const key = data.fields.key!;

  // Local echo (file-handling §14.3.6): every successful upload — every
  // surface, no per-surface wiring — hands the bytes to the echo store so
  // `useFileRef` can render them during the server's processing window.
  // Registering here rather than at each call site is the whole point: a
  // surface that forgets gets instant visibility anyway.
  registerLocalEcho(key, file);

  return key;
}
